import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectsQuery } from '@/features/projects';
import { useUsersQuery } from '@/features/users';
import { useOrganisationEntitlements } from '@/shared/hooks';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { BarChart, Box, Chip, Inline, Panel, Section, Stack, Text } from '@/ui';
import { PageShell } from '@/ui/primitives/PageShell';
import type { Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

type UsageBarProps = {
  value: number | null;
  limit: number | null;
  periodEnd: Date | null;
  showReset?: boolean;
  showRemaining?: boolean;
  barTone?: 'default' | 'purple' | 'pink';
};

const UsageBar = ({
  value,
  limit,
  periodEnd,
  showReset = true,
  showRemaining = true,
  barTone = 'default',
}: UsageBarProps): ReactElement => {
  const formatResetAt = (periodValue: Date | string | null): string => {
    if (!periodValue) return '-';
    const dateValue = periodValue instanceof Date ? periodValue : new Date(periodValue);
    if (Number.isNaN(dateValue.valueOf())) return '-';
    return dateValue.toLocaleString();
  };
  const remainingTextSx = (theme: Theme): Record<string, string | number> => ({
    fontSize: theme.typography.body.fontSize ?? 'inherit',
    lineHeight: theme.typography.body.lineHeight ?? 1.6,
    fontWeight: theme.typography.body.fontWeight ?? 400,
  });
  const resolvedValue = value ?? 0;
  const resolvedLimit = limit ?? null;
  const remainingPercent =
    resolvedLimit && resolvedLimit > 0
      ? Math.max(0, Math.round(((resolvedLimit - resolvedValue) / resolvedLimit) * 100))
      : null;
  const percent =
    resolvedLimit && resolvedLimit > 0
      ? Math.min(resolvedValue / resolvedLimit, 1)
      : resolvedLimit === null
        ? 1
        : 0;
  const resolvedLabel =
    resolvedLimit === null
      ? 'Unlimited'
      : `${resolvedValue} / ${resolvedLimit}${
          showRemaining ? ' executions' : barTone === 'pink' ? ' projects' : ' users'
        }`;

  return (
    <Stack gap="md">
      {showRemaining ? (
        <Text role="body" sx={remainingTextSx}>
          {remainingPercent !== null ? `${remainingPercent}%` : '-'} remaining
        </Text>
      ) : null}
      <Box
        sx={(theme) => ({
          width: '100%',
          height: s('md'),
          backgroundColor: theme.app.colors.border,
          borderRadius: r('md'),
          overflow: 'hidden',
        })}
      >
        <Box
          sx={(theme) => ({
            width: `${Math.round(percent * 100)}%`,
            height: '100%',
            backgroundColor:
              barTone === 'purple'
                ? uiTokens.statusColors.purple
                : barTone === 'pink'
                  ? 'var(--color-pink)'
                  : theme.app.colors.primary,
            borderRadius: r('md'),
          })}
        />
      </Box>
      <Inline justify="space-between" align="center">
        {showReset ? (
          <Text role="body" color="textSecondary">
            Resets at {formatResetAt(periodEnd)}
          </Text>
        ) : (
          <Box />
        )}
        <Text role="body" color="textSecondary">
          {resolvedLabel}
        </Text>
      </Inline>
    </Stack>
  );
};

export const PlanPage = (): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  useAuth();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const projectsQuery = useProjectsQuery({ refetchOnWindowFocus: false });
  const usersQuery = useUsersQuery();
  const entitlements = entitlementsQuery.data ?? null;
  const usage = entitlements?.usage ?? null;
  const executionDailyBreakdown = usage?.executionDailyBreakdown ?? [];
  const today = new Date();
  const todayUtcDay = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
  const executionCountByDay = new Map<string, number>();
  const suiteExecutionCountByDay = new Map<string, number>();
  const testExecutionCountByDay = new Map<string, number>();
  executionDailyBreakdown.forEach((entry) => {
    const day = entry.day instanceof Date ? entry.day : new Date(entry.day);
    if (Number.isNaN(day.valueOf())) return;
    const dayKey = day.toISOString().slice(0, 10);
    executionCountByDay.set(dayKey, entry.executionCount);
    suiteExecutionCountByDay.set(dayKey, entry.suiteExecutionCount);
    testExecutionCountByDay.set(dayKey, entry.testExecutionCount);
  });
  const executionDailyBreakdownRecent = Array.from({ length: 7 }, (_, index) => {
    const offset = 6 - index;
    const day = new Date(todayUtcDay);
    day.setUTCDate(day.getUTCDate() - offset);
    const dayKey = day.toISOString().slice(0, 10);
    return {
      day,
      executionCount: executionCountByDay.get(dayKey) ?? 0,
      suiteExecutionCount: suiteExecutionCountByDay.get(dayKey) ?? 0,
      testExecutionCount: testExecutionCountByDay.get(dayKey) ?? 0,
    };
  });
  const executionDailyChartMax = executionDailyBreakdownRecent.reduce(
    (max, entry) => (entry.executionCount > max ? entry.executionCount : max),
    0,
  );
  const executionDailyChartData = executionDailyBreakdownRecent.map((entry) => {
    const value = entry.day instanceof Date ? entry.day : new Date(entry.day);
    const label = Number.isNaN(value.valueOf())
      ? String(entry.day)
      : value.toISOString().slice(0, 10);
    return {
      label,
      executionCount: entry.executionCount,
      suiteExecutionCount: entry.suiteExecutionCount,
      testExecutionCount: entry.testExecutionCount,
    };
  });
  const executionDailyTooltipBreakdownByLabel = Object.fromEntries(
    executionDailyChartData.map((entry) => [
      entry.label,
      {
        total: entry.executionCount,
        rows: [
          {
            label: 'Suites',
            value: entry.suiteExecutionCount,
            color: uiTokens.statusColors.teal,
          },
          {
            label: 'Tests',
            value: entry.testExecutionCount,
            color: uiTokens.statusColors.purple,
          },
        ],
      },
    ]),
  );
  const trialEndsAt = entitlements?.trialEndsAt ?? usage?.periodEnd ?? null;
  const projectCount = projectsQuery.data?.total ?? null;
  const userCount = usersQuery.data?.total ?? null;
  const formatPlanName = (value: string | null | undefined): string => {
    if (!value) return 'Loading';
    if (value === 'starter') return 'Core';
    return value.toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase());
  };
  const formatDate = (value: Date | string | null | undefined): string => {
    if (!value) return '-';
    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.valueOf())) return '-';
    return dateValue.toLocaleDateString();
  };
  const isTrialPlan = entitlements?.plan.code === 'free';
  const formatNumber = (value: number | null | undefined): string =>
    typeof value === 'number' ? new Intl.NumberFormat('en-US').format(value) : '-';

  return (
    <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="lg">
      <Stack gap="xl">
        <Section>
          <Stack gap="lg">
            <Text role="pageTitle" component="h1">
              Plan Settings
            </Text>
            <Panel
              title="Plan Details"
              titleSlot={
                <Chip
                  label={formatPlanName(entitlements?.plan.name)}
                  size="small"
                  tone="default"
                  transform="none"
                />
              }
            >
              <Stack gap="md">
                <Text role="label">Limits</Text>
                <Inline justify="space-between" align="center">
                  <Text role="body" color="textSecondary">
                    {isTrialPlan ? 'Trial Started' : 'Plan Started'}
                  </Text>
                  <Text role="body">{formatDate(usage?.periodStart ?? null)}</Text>
                </Inline>
                {isTrialPlan ? (
                  <Inline justify="space-between" align="center">
                    <Text role="body" color="textSecondary">
                      Trial Ends
                    </Text>
                    <Text role="body">{formatDate(trialEndsAt)}</Text>
                  </Inline>
                ) : null}
                <Inline justify="space-between" align="center">
                  <Text role="body" color="textSecondary">
                    Projects
                  </Text>
                  <Text role="body">{entitlements?.plan.limits.projectLimit ?? '-'}</Text>
                </Inline>
                <Inline justify="space-between" align="center">
                  <Text role="body" color="textSecondary">
                    Users
                  </Text>
                  <Text role="body">{entitlements?.plan.limits.userLimit ?? '-'}</Text>
                </Inline>
                <Inline justify="space-between" align="center">
                  <Text role="body" color="textSecondary">
                    {isTrialPlan
                      ? 'Trial Execution Uploads'
                      : usage?.periodType === 'weekly'
                        ? 'Weekly Execution Uploads'
                        : usage?.periodType === 'monthly'
                          ? 'Monthly Execution Uploads'
                          : 'Execution Uploads'}
                  </Text>
                  <Text role="body">
                    {usage?.periodType === 'weekly'
                      ? formatNumber(entitlements?.plan.limits.executionLimitWeekly ?? null)
                      : usage?.periodType === 'monthly'
                        ? formatNumber(entitlements?.plan.limits.executionLimitMonthly ?? null)
                        : '-'}
                  </Text>
                </Inline>
              </Stack>
            </Panel>
            <Panel title="Projects">
              <UsageBar
                value={projectCount}
                limit={entitlements?.plan.limits.projectLimit ?? null}
                periodEnd={null}
                showReset={false}
                showRemaining={false}
              />
            </Panel>
            <Panel title="Users">
              <UsageBar
                value={userCount}
                limit={entitlements?.plan.limits.userLimit ?? null}
                periodEnd={null}
                showReset={false}
                showRemaining={false}
                barTone="purple"
              />
            </Panel>
            <Panel
              title={
                isTrialPlan
                  ? 'Trial Execution Uploads'
                  : usage?.periodType === 'weekly'
                    ? 'Weekly Execution Uploads'
                    : usage?.periodType === 'monthly'
                      ? 'Monthly Execution Uploads'
                      : 'Execution Uploads'
              }
            >
              <Stack gap="md">
                <UsageBar
                  value={usage?.executionCount ?? null}
                  limit={usage?.executionLimit ?? null}
                  periodEnd={usage?.periodEnd ?? null}
                  showReset={!isTrialPlan}
                  barTone="pink"
                />
                <Stack gap="xs">
                  <Text role="label">Execution Uploads Per Day</Text>
                  {executionDailyBreakdownRecent.length > 0 ? (
                    <BarChart
                      data={executionDailyChartData}
                      series={[
                        {
                          key: 'executionCount',
                          label: '',
                          color: uiTokens.statusColors.pink,
                        },
                      ]}
                      height={220}
                      showLegend={false}
                      hideYAxis
                      hideXAxisLine={false}
                      showHorizontalGrid={false}
                      tooltipBreakdownByLabel={executionDailyTooltipBreakdownByLabel}
                      yAxisMin={0}
                      yAxisMax={Math.max(1, executionDailyChartMax)}
                      xTickEvery={3}
                    />
                  ) : (
                    <Text role="body" color="textSecondary">
                      No execution uploads recorded in this period.
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Panel>
          </Stack>
        </Section>
      </Stack>
    </PageShell>
  );
};
